import React, { useRef, useEffect } from 'react';
import { Language, MenuCategory } from '../types';
import { smoothScrollTo } from '../src/utils/smoothScroll';

interface CategoryNavProps {
  categories: MenuCategory[];
  activeCategory: string;
  onSelectCategory: (id: string) => void;
  language: Language;
}

/**
 * Липкий бар навигации по категориям со scroll-spy. Пиннится к верху
 * скролл-контейнера, когда гость прокручивает в список секций. Активная
 * вкладка (её задаёт scroll-spy в Home) авто-центрируется в баре.
 *
 * Категории приходят живыми из filteredMenuData — при активных фильтрах
 * показываются только те секции, что реально есть на экране, чтобы тап
 * всегда куда-то приземлялся.
 */
const CategoryNav: React.FC<CategoryNavProps> = ({ categories, activeCategory, onSelectCategory, language }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const activeEl = container.querySelector<HTMLElement>(`[data-cat="${activeCategory}"]`);
    if (activeEl) {
      const scrollLeft = activeEl.offsetLeft - container.offsetWidth / 2 + activeEl.offsetWidth / 2;
      // rAF-скролл: native smooth в части webview — no-op, чип не центрировался бы.
      smoothScrollTo(container, scrollLeft, { axis: 'left', duration: 300 });
    }
  }, [activeCategory]);

  if (categories.length < 2) return null;

  return (
    <div className="sticky top-0 z-30 -mx-5 px-5 py-2.5 bg-[var(--bg-primary-95)] backdrop-blur-md border-b border-[var(--glass-border)]">
      <div ref={scrollContainerRef} className="flex gap-2 overflow-x-auto no-scrollbar">
        {categories.map((category) => {
          const active = activeCategory === category.id;
          return (
            <button
              key={category.id}
              data-cat={category.id}
              onClick={() => onSelectCategory(category.id)}
              className={`whitespace-nowrap flex-shrink-0 px-3.5 py-2 rounded-full text-xs font-bold transition-colors ${
                active
                  ? 'bg-[var(--accent-color)] text-white shadow-md shadow-[var(--accent-15)]'
                  : 'bg-[var(--bg-card)] text-[var(--text-secondary)] border border-[var(--glass-border)] hover:border-[var(--accent-20)]'
              }`}
            >
              {category.names?.[language] || category.names?.ru}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CategoryNav;
